import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Autosuggest from 'react-autosuggest';
import debounce from 'lodash/debounce';
import { searchTags } from '../../../utils/api/tagsApi';

import './index.less';
import TagList from './TagList';
import { isEnterPressEvent, isSpacebarPressEvent } from '../../../utils/keyboardHelper';
import { stripSpecialChars } from '../../../utils/stringUtil';

/**
 * Require that tags of meaning have at least length 3
 * */
const MIN_VALID_TAG_LENGTH = 3;

const isValidMinLength = (tag) => tag.trim().length >= MIN_VALID_TAG_LENGTH;

const getSuggestions = (term) => {
  if (!isValidMinLength(term) || term.trim().length > 128) {
    console.warn(`Search term was too short or too long (length: ${term.trim().length})`);
    return Promise.resolve([]);
  }

  return searchTags(term);
};

/**
 * Determines if react-autosuggest should
 * render suggestions. Our lowest threshold is
 * 3 characters, anything less is probably noise
 * */
const shouldRenderSuggestion = (value) => isValidMinLength(value);

const TagSelectField = ({ onChange, inputName }) => {
  // List of suggested tags
  const [suggestions, updateSuggestion] = useState([]);
  // List of selected tags
  const [tags, updateTags] = useState([]);
  // Value for input field
  const [fieldValue, updateField] = useState('');

  function rememberTag(tag) {
    // Ensure tags selected are of valid length
    if (!isValidMinLength(tag)) {
      return;
    }

    updateTags((stored) => {
      if (!stored.includes(tag)) {
        stored.push(tag);
      }
      return stored;
    });

    // When a tag is selected, clear the field
    updateField('');
  }

  useEffect(() => {
    onChange(tags);

    // This should only be called if the size of tags has changed
    // which is adequate, given that tag-operations are: 'push' and 'remove'
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tags.length]);

  useEffect(() => {
    const handleKeyPress = (evt) => {
      if (isEnterPressEvent(evt) || isSpacebarPressEvent(evt)) {
        // Ensure form is not accidentally submitted
        evt.preventDefault();

        // User intends to add a new custom tag
        const customTag = evt.target.value;
        rememberTag(customTag);
      }
    };

    const input = document.getElementsByName(inputName)[0];

    input.addEventListener('keypress', handleKeyPress);

    return () => {
      input.removeEventListener('keypress', handleKeyPress);
    };
  }, [inputName]);

  // Properties forwarded to input field in react-autosuggest
  const inputProps = {
    placeholder: 'Skriv inn fagkode, studie eller annet. Som "tdt4110"',
    value: fieldValue,
    onChange: (evt, { newValue }) => {
      // Deny the use of whitespace in tags
      const sanitized = stripSpecialChars(newValue);

      updateField(sanitized);
    },
    name: inputName,
  };

  return <div>
    {tags.length > 0 && <TagList tags={tags} onClick={({ label }) => {
      updateTags((stored) => stored.filter((item) => item !== label));
    }}/>}

    <Autosuggest
      suggestions={suggestions}
      shouldRenderSuggestions={shouldRenderSuggestion}
      onSuggestionsFetchRequested={debounce(({ value }) => {
        // No need to ask for the same data.
        // fieldValue will be the 'previous value'
        if (fieldValue.trim() === value.trim()) {
          return;
        }

        getSuggestions(value).then((serverSuggestions) => updateSuggestion(serverSuggestions));
      }, 300)}
      onSuggestionSelected={(evt, { suggestion }) => rememberTag(suggestion)}
      onSuggestionsClearRequested={() => updateSuggestion([])}
      getSuggestionValue={(suggestion) => suggestion}
      renderSuggestion={(suggestion) => <p>{suggestion}</p>}
      inputProps={inputProps}
    />
  </div>;
};

TagSelectField.propTypes = {
  onChange: PropTypes.func.isRequired,
  inputName: PropTypes.string.isRequired,
};

export default TagSelectField;
